import axios, { AxiosResponse, AxiosError } from 'axios';
import toast from 'react-hot-toast';
import config from '@/config';
import { SIGNED_OUT_REASON_KEY, withIdleHeader } from '@/services/activity';
import { 
  Account, 
  AccountingPeriod, 
  Subsidiary, 
  ReconciliationSummary,
  AuthStatus,
  DashboardStats
} from '@/types';

/** F6: the server refuses a POST/PUT/PATCH/DELETE without this header (backend app/security/csrf.py). Another
 *  site can't add a custom header to a request that carries our cookies without a CORS preflight we don't answer. */
export const APP_REQUEST_HEADERS = { 'X-MatchPoint-Request': '1' } as const;

// Create axios instance with default configuration
export const api = axios.create({
  baseURL: '', // Use relative URLs - Vite proxy handles routing to backend
  timeout: config.apiTimeout,
  withCredentials: true, // Important: Send cookies with requests
  headers: {
    'Content-Type': 'application/json',
    ...APP_REQUEST_HEADERS,
  },
});

// F9: every request says how long this tab has had no input, so polling doesn't keep an unattended sign-in alive
withIdleHeader(api);

export const SERVER_ERROR_TOAST = 'server-error';
export const TIMEOUT_TOAST = 'request-timeout';
export const BUSY_TOAST = 'server-busy';

/** The response interceptor's error handler, exported for its test. */
export const onResponseError = (error: AxiosError) => {
  if (error.response?.status === 401) {
    // F9: signed out by a time limit. The sign-in page says why (idle or the absolute limit).
    const body = error.response.data as { code?: string; reason?: string; tries_left?: number } | undefined;
    if (body?.code === 'SESSION_EXPIRED') {
      try { sessionStorage.setItem(SIGNED_OUT_REASON_KEY, body.reason === 'absolute' ? 'absolute' : 'idle'); }
      catch { /* storage blocked: the sign-in page just doesn't say why */ }
    }
    // Don't redirect if this is the auth status check - let the app handle it. Nor on a wrong password or two-step
    // code: going to /login reloads the sign-in page, which loses its message and the two-step screen.
    const wrongAttempt = error.config?.url === '/auth/login' || typeof body?.tries_left === 'number'
      || body?.code === 'MFA_EXPIRED';             // TwoFactor goes back to the form and says why
    if (!error.config?.url?.includes('/auth/status') && !wrongAttempt) {
      window.location.href = '/login';
    }
  } else if (error.response?.status === 403
             && (error.response.data as { code?: string } | undefined)?.code === 'PASSWORD_CHANGE_REQUIRED') {
    // An administrator set this password: the app shows the "choose a new password" screen until it's changed
    if (window.location.pathname !== '/') window.location.href = '/';
  } else if (error.response?.status === 429
             && typeof (error.response.data as { error?: unknown } | undefined)?.error !== 'string') {
    // nginx's rate limit (an HTML page, no JSON `error`): too many requests from this address at once. The app's
    // own 429s (sign-in attempts) carry an `error` the page shows itself.
    toast.error('MatchPoint is busy. Please wait a few seconds and try again.', { id: BUSY_TOAST });
  } else if (error.response && [502, 503, 504].includes(error.response.status)) {
    // A restart during a release, or the database's time limit (DATABASE_BUSY says nothing was saved)
    const body = error.response.data as { code?: string; message?: string } | undefined;
    toast.error(body?.code === 'DATABASE_BUSY' && body.message ? body.message
      : 'MatchPoint is busy or restarting. Please try again in a moment.', { id: BUSY_TOAST });
  } else if (error.response && error.response.status >= 500) {
    // F1: one toast, not one per request. React Query retries a failed query once, and a page often fires several
    // queries that fail together: a fixed id makes react-hot-toast update the toast already showing.
    toast.error('Server error. Please try again later.', { id: SERVER_ERROR_TOAST });
  } else if (error.code === 'ECONNABORTED') {
    toast.error('Request timeout. Please check your connection.', { id: TIMEOUT_TOAST });
  }
  return Promise.reject(error);
};

// Response interceptor for error handling
api.interceptors.response.use((response: AxiosResponse) => response, onResponseError);

// API Service class
class ApiService {
  // Health and info endpoints
  async getHealth() {
    const response = await api.get('/api/health');
    return response.data;
  }

  async getApiInfo() {
    const response = await api.get('/api/info');
    return response.data;
  }

  // Authentication endpoints
  async getAuthStatus(): Promise<AuthStatus> {
    const response = await api.get('/auth/status');
    return response.data;
  }

  async loginWithOkta() {
    const response = await api.post('/auth/login/okta');
    
    // Redirect to Okta authorization URL
    if (response.data.authorization_url) {
      window.location.href = response.data.authorization_url;
    }
    
    return response.data;
  }

  async logout() {
    const response = await api.post('/auth/logout');
    // Follow provider logout URL if supplied (Microsoft/Okta SSO sign-out)
    const redirectUrl = response.data?.redirect_url;
    window.location.href = redirectUrl && redirectUrl !== window.location.origin + '/login'
      ? redirectUrl
      : '/login';
    return response.data;
  }

  // Account endpoints
  async getAccounts(): Promise<Account[]> {
    // Get all accounts with their template categories
    const response = await api.get('/api/account/with-categories');
    return response.data.account || [];
  }

  async getAccount(id: number): Promise<Account> {
    const response = await api.get(`/api/accounts/${id}`);
    return response.data.account;
  }

  async getBalanceSheetAccountConfigs(subsidiary_id: number, includeInactive: boolean = true): Promise<any> {
    const params: any = { subsidiary_id };
    if (includeInactive) params.include_inactive = 'true';

    console.log('API getBalanceSheetAccountConfigs - params:', params);

    const response = await api.get('/api/balance-sheet-account', { params });
    return response.data;
  }

  // Template Category endpoints
  async getAccountsWithCategories() {
    const response = await api.get('/api/account/with-categories');
    return response.data;
  }

  async getTemplateCategories() {
    const response = await api.get('/api/account/template-categories');
    return response.data;
  }

  async updateAccountTemplateCategory(account_id: number | undefined, templateCategory: string) {
    const response = await api.put(`/api/account/${account_id}/template-category`, {
      template_category: templateCategory
    });
    return response.data;
  }

  async bulkUpdateTemplateCategories(data: { updates: Array<{ account_id: number; template_category: string }> }) {
    const response = await api.post('/api/account/bulk-update-categories', data);
    return response.data;
  }

  // Period endpoints
  async getAccountingPeriods() {
    const response = await api.get('/api/periods');
    return response.data;
  }

  async getCurrentPeriod(): Promise<AccountingPeriod | null> {
    const response = await this.getAccountingPeriods();
    const periods = response.periods || [];
    return periods.find((p: AccountingPeriod) => p.status === 'OPEN') || null;
  }

  // Subsidiary endpoints
  async getSubsidiaries(): Promise<Subsidiary[]> {
    const response = await api.get('/api/subsidiaries');
    return response.data.subsidiaries || [];
  }

  // Reconciliation endpoints
  async getReconciliationAccounts(subsidiary_id?: number, periodId?: number | null, roleView?: string): Promise<any> {
    const params: any = {};
    if (subsidiary_id && subsidiary_id > 0) params.subsidiary_id = subsidiary_id;
    if (periodId) params.period_id = periodId;
    if (roleView) params.role_view = roleView;
    
    const response = await api.get('/api/reconciliation/list', { params });     // was /test/accounts (not a test)
    return response.data;
  }

  async getReconciliationSummary(subsidiary_id?: number, periodId?: number | null): Promise<ReconciliationSummary> {
    const params: any = {};
    if (subsidiary_id && subsidiary_id > 0) params.subsidiary_id = subsidiary_id;
    if (periodId) params.period_id = periodId;
    const response = await api.get('/api/reconciliation/reconciliation/summary', { params });
    return response.data || {
      total_accounts: 0,
      not_started: 0,
      in_progress: 0,
      prepared: 0,
      approved: 0,
      rejected: 0,
      overdue: 0,
      due_soon: 0,
    };
  }

  async getDashboardStats(subsidiary_id?: number, periodId?: number | null): Promise<DashboardStats> {
    const params: any = {};
    if (subsidiary_id) params.subsidiary_id = subsidiary_id;
    if (periodId) params.period_id = periodId;
    const response = await api.get('/api/reconciliation/dashboard/stats', { params });
    return response.data;
  }

  async getRecentActivity(roleView?: string, limit = 10, subsidiary_id?: number, periodId?: number | null) {
    const params: any = { role_view: roleView || 'full', limit };
    if (subsidiary_id) params.subsidiary_id = subsidiary_id;
    if (periodId) params.period_id = periodId;
    const response = await api.get('/api/reconciliation/dashboard/recent-activity', { params });
    return response.data;
  }

  async getWorkQueue(roleView?: string, limit = 10, subsidiary_id?: number, periodId?: number | null) {
    const params: any = { role_view: roleView || 'full', limit };
    if (subsidiary_id) params.subsidiary_id = subsidiary_id;
    if (periodId) params.period_id = periodId;
    const response = await api.get('/api/reconciliation/dashboard/work-queue', { params });
    return response.data;
  }

  // Account configuration endpoint (general balance sheet account updates)
  async updateBalanceSheetAccount(account_id: number | undefined, updates: any) {
    const response = await api.put(`/api/balance-sheet-accounts/${account_id}`, updates);
    return response.data;
  }

  // Reconciliation workflow endpoints
  async startReconciliation(account_id: number | undefined, periodId: number) {
    const response = await api.post(`/reconciliation/accounts/${account_id}/start`, {
      period_id: periodId,
    });
    return response.data;
  }

  async submitForApproval(account_id: number | undefined, periodId: number) {
    const response = await api.post(`/reconciliation/accounts/${account_id}/submit`, {
      period_id: periodId,
    });
    return response.data;
  }


  // Attachment endpoints
  async deleteAttachment(_reconciliationId: number, attachmentId: number) {
    const response = await api.delete(
      `/api/reconciliation/attachments/${attachmentId}`
    );
    return response.data;
  }

  async carryForwardAttachments(reconciliationId: number) {
    const response = await api.post(
      `/api/reconciliation/reconciliations/${reconciliationId}/attachments/carry-forward`
    );
    return response.data;
  }

  // Carry-forward endpoints
  async carryForwardReconciliation(reconciliationId: number, options?: {
    source_reconciliation_id?: number;
    include_all_items?: boolean;
    carry_explanation?: boolean;
    carry_comments?: boolean;
    copy_attachments?: boolean;
    copy_open_items?: boolean;
    copy_comments?: boolean;
  }) {
    const response = await api.post(
      `/api/reconciliation/reconciliations/${reconciliationId}/carry-forward`,
      options || {}
    );
    return response.data;
  }

  async autoCertifyReconciliation(reconciliationId: number) {
    const response = await api.post(
      `/api/reconciliation/reconciliations/${reconciliationId}/auto-certify`
    );
    return response.data;
  }

  async getReconciliationHistory(account_id?: number) {
    const response = await api.get(
      `/api/reconciliation/accounts/${account_id}/reconciliation-history`
    );
    return response.data;
  }

  // Reconciliation detail endpoints
  async getReconciliationDetail(reconciliationId: number) {
    const response = await api.get(`/api/reconciliation/reconciliations/${reconciliationId}`);
    return response.data;
  }

  // Transaction endpoints
  async getAccountTransactions(reconciliationId: number, params?: {
    page?: number;
    per_page?: number;
    search?: string;
    type?: string;          // one transaction type (the by-type summary's NO_TYPE for lines with none)
  }) {
    const response = await api.get(`/api/reconciliation/${reconciliationId}/transactions`, { params });
    return response.data;
  }

  async addReconciliationItem(reconciliationId: number, data: any) {
    const response = await api.post(`/api/reconciliation/reconciliations/${reconciliationId}/items`, data);
    return response.data;
  }

  async updateReconciliationItem(reconciliationId: number, itemId: number, data: any) {
    const response = await api.put(`/api/reconciliation/reconciliations/${reconciliationId}/items/${itemId}`, data);
    return response.data;
  }

  async deleteReconciliationItem(reconciliationId: number, itemId: number) {
    const response = await api.delete(`/api/reconciliation/reconciliations/${reconciliationId}/items/${itemId}`);
    return response.data;
  }

  async addReconciliationComment(reconciliationId: number, comment: string) {
    const response = await api.post(`/api/reconciliation/reconciliations/${reconciliationId}/comments`, {
      comment_text: comment,
    });
    return response.data;
  }

  // Reconciliation workflow endpoints
  async submitReconciliation(reconciliationId: number) {
    const response = await api.post(`/api/reconciliation/reconciliations/${reconciliationId}/submit`);
    return response.data;
  }

  /** R-04: confirmBalanceChange once the approver has seen that the balance moved since submission (else 409). */
  async approveReconciliation(reconciliationId: number, comments?: string, confirmBalanceChange = false) {
    const response = await api.post(`/api/reconciliation/reconciliations/${reconciliationId}/approve`, {
      comments: comments || 'Approved via balance sheet signoff system',
      ...(confirmBalanceChange ? { confirm_balance_change: true } : {}),
    });
    return response.data;
  }

  async rejectReconciliation(reconciliationId: number, reason: string) {
    const response = await api.post(`/api/reconciliation/reconciliations/${reconciliationId}/reject`, {
      comments: reason
    });
    return response.data;
  }

  async reopenReconciliation(reconciliationId: number, reason: string) {
    const response = await api.post(`/api/reconciliation/reconciliations/${reconciliationId}/reopen`, {
      reason
    });
    return response.data;
  }

  // Period management endpoints (duplicate removed)

  /** D1 (UX review): the subsidiary and period a first visit opens on (the user's company, a month with recs). */
  async getDefaultContext(): Promise<{ subsidiary_id: number | null; period_id: number | null }> {
    const response = await api.get('/api/reconciliation/default-context');
    return response.data;
  }

  async getReconciliationPeriods(subsidiary_id?: number) {
    const params = subsidiary_id && subsidiary_id > 0 ? { subsidiary_id: subsidiary_id } : {};
    const response = await api.get('/api/reconciliation/periods', { params });
    return response.data;
  }

  async createReconciliationPeriod(accountingPeriodId: number) {
    const response = await api.post('/api/reconciliation/periods/create', {
      accounting_period_id: accountingPeriodId,
    });
    return response.data;
  }

  async lockPeriod(periodId: number, options?: { force?: boolean; reason?: string }) {
    const response = await api.post(`/api/reconciliation/periods/${periodId}/lock`, options || {});
    return response.data;
  }

  async unlockPeriod(periodId: number, reason: string) {
    const response = await api.post(`/api/reconciliation/periods/${periodId}/unlock`, {
      reason,
    });
    return response.data;
  }

  async closePeriod(periodId: number) {
    const response = await api.post(`/api/reconciliation/periods/${periodId}/close`);
    return response.data;
  }

  async reopenPeriod(periodId: number, reason: string) {
    const response = await api.post(`/api/reconciliation/periods/${periodId}/reopen`, {
      reason,
    });
    return response.data;
  }

  async deletePeriod(periodId: number) {
    const response = await api.delete(`/api/reconciliation/periods/${periodId}`);
    return response.data;
  }

  async getPeriodStatus(periodId: number) {
    const response = await api.get(`/api/reconciliation/periods/${periodId}/status`);
    return response.data;
  }

  async getPeriodStatistics(periodId: number) {
    const response = await api.get(`/api/reconciliation/periods/${periodId}/status`);
    return response.data.statistics;
  }


  async bulkCreatePeriods(accountingPeriodIds: number[]) {
    const response = await api.post('/api/reconciliation/periods/bulk-create', {
      accounting_period_ids: accountingPeriodIds,
    });
    return response.data;
  }

  // Attachment endpoints
  async uploadAttachment(reconciliationId: number, file: File, description?: string, attachmentType?: string) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('description', description || file.name);
    formData.append('attachment_type', attachmentType || 'supporting_document');
    
    const response = await api.post(
      `/api/reconciliation/reconciliations/${reconciliationId}/attachments`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  }

  async downloadAttachment(_reconciliationId: number, attachmentId: number, fileName: string) {
    const response = await api.get(
      `/api/reconciliation/attachments/${attachmentId}`, 
      {
        responseType: 'blob',
      }
    );
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  // Item attachment methods
  async uploadItemAttachment(_reconciliationId: number, itemId: number, file: File) {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post(
      `/api/reconciliation/items/${itemId}/attachment`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000
      }
    );
    return response.data;
  }

  async downloadItemAttachment(itemId: number) {
    const response = await api.get(`/api/reconciliation/items/${itemId}/attachment`, {
      responseType: 'blob',
      timeout: 60000
    });
    return response.data;
  }

  async deleteItemAttachment(_reconciliationId: number, itemId: number) {
    const response = await api.delete(`/api/reconciliation/items/${itemId}/attachment`);
    return response.data;
  }

  // User management endpoints
  async getUsers() {
    const response = await api.get('/auth/users');
    return response.data;
  }

  /** K-07: the Audit trail's user filter, for admins and auditors (not the admin user list). */
  async getAuditActors(): Promise<{ users: { id: number; email: string | null; label: string }[] }> {
    const response = await api.get('/auth/audit/actors');
    return response.data;
  }

  async getUser(userId: number) {
    const response = await api.get(`/auth/users/${userId}`);
    return response.data;
  }

  async createUser(data: any) {
    const response = await api.post('/auth/users', data);
    return response.data;
  }

  async updateUser(userId: number, data: any) {
    const response = await api.put(`/auth/users/${userId}`, data);
    return response.data;
  }

  async deleteUser(userId: number) {
    const response = await api.delete(`/auth/users/${userId}`);
    return response.data;
  }

  async getRoles() {
    const response = await api.get('/auth/roles');
    return response.data;
  }

  async checkUserAssignments(userId: number) {
    const response = await api.get(`/auth/users/${userId}/check-assignments`);
    return response.data;
  }

  // User assignment endpoints
  async getAccountAssignments(params?: {
    subsidiary_id?: number;
    search?: string;
    has_preparer?: boolean;
    has_approver?: boolean;
  }) {
    const response = await api.get('/auth/assignments', { params });
    return response.data;
  }

  async updateAccountAssignment(account_id: number | undefined, data: {
    preparer_id?: number | null;
    approver_id?: number | null;
    backup_preparer_id?: number | null;
    backup_approver_id?: number | null;
  }) {
    const response = await api.put(`/auth/assignments/${account_id}`, data);
    return response.data;
  }

  async bulkUpdateAssignments(data: {
    account_ids: number[];
    updates: {
      preparer_id?: number | null;
      approver_id?: number | null;
      backup_preparer_id?: number | null;
      backup_approver_id?: number | null;
    };
  }) {
    const response = await api.put('/auth/assignments/bulk', data);
    return response.data;
  }

  async getUsersByRole(role?: string) {
    const response = await api.get('/auth/users/by-role', { 
      params: { role } 
    });
    return response.data;
  }

  // Account configuration endpoints
  async updateAccountConfiguration(account_id: number | undefined, updates: {
    reconciliation_required?: boolean;
    risk_level?: string;
    due_days_after_period_end?: number;
  }) {
    const response = await api.put(`/api/reconciliation/accounts/${account_id}/configuration`, updates);
    return response.data;
  }

  // Carry-forward endpoints
  async getCarryForwardPreview(reconciliationId: number) {
    const response = await api.get(`/api/reconciliation/reconciliations/${reconciliationId}/copy-preview`);
    return response.data;
  }

  async copyReconciliation(reconciliationId: number, options: {
    target_period_id: number;
    copy_attachments?: boolean;
    copy_open_items?: boolean;
    copy_comments?: boolean;
    enable_auto_certification?: boolean;
  }) {
    const response = await api.post(`/api/reconciliation/reconciliations/${reconciliationId}/copy`, options);
    return response.data;
  }

  async bulkCopyPeriod(targetPeriodId: number, sourcePeriodId: number, options?: {
    copy_attachments?: boolean;
    copy_open_items?: boolean;
    copy_comments?: boolean;
    enable_auto_certification?: boolean;
  }) {
    const response = await api.post(`/api/reconciliation/periods/${targetPeriodId}/bulk-copy`, {
      source_period_id: sourcePeriodId,
      ...options
    });
    return response.data;
  }

  async getCarryForwardInfo(reconciliationId: number) {
    const response = await api.get(`/api/reconciliation/reconciliations/${reconciliationId}/carry-forward-info`);
    return response.data;
  }

  // Audit trail endpoints
  async getAuditLogs(params?: {
    page?: number;
    per_page?: number;
    user_id?: number;
    action?: string;
    resource_type?: string;
    resource_id?: number;
    success?: boolean;
    start_date?: string;
    end_date?: string;
  }) {
    const response = await api.get('/auth/audit/logs', { params });
    return response.data.logs || [];
  }

  async getAuditSummary(days?: number) {
    const response = await api.get('/auth/audit/summary', { 
      params: { days: days || 30 } 
    });
    return response.data;
  }

  async getReconciliationAuditTrail(reconciliationId: number) {
    const response = await api.get(`/auth/audit/reconciliation/${reconciliationId}`);
    return response.data;
  }

  async getUserAuditTrail(userId: number, params?: {
    page?: number;
    per_page?: number;
    days?: number;
  }) {
    const response = await api.get(`/auth/audit/user/${userId}`, { params });
    return response.data;
  }

  async exportAuditLogs(data: {
    format: 'csv' | 'json';
    start_date?: string;
    end_date?: string;
    filters?: Record<string, any>;
  }) {
    const response = await api.post('/auth/audit/export', data);
    return response.data;
  }

  // Auto-certification rules endpoints
  async getAutoCertRules() {
    const response = await api.get('/api/admin/auto-cert-rules/');
    return response.data;
  }

  async updateAutoCertRules(config: any) {
    const response = await api.put('/api/admin/auto-cert-rules/', { config });
    return response.data;
  }

  async getAutoCertRulesDefaults() {
    const response = await api.get('/api/admin/auto-cert-rules/defaults');
    return response.data;
  }

  async previewAutoCert(criteria?: Record<string, boolean>) {
    const response = await api.post('/api/admin/auto-cert-rules/preview', criteria ? { criteria } : {});
    return response.data;
  }

  async triggerAutoCert() {
    const response = await api.post('/api/admin/auto-cert-rules/trigger');
    return response.data;
  }

  async getEnabledModules(): Promise<{ enabled: import('@/types').ModuleInfo[]; access?: string[]; available?: string[]; new_user_defaults?: string[] }> {
    const response = await api.get('/api/modules');
    return response.data;
  }
}

// Export singleton instance
export const apiService = new ApiService();
export default api;